import { useEffect, useRef, useState } from 'react';
import { getInitialActiveSectionKey } from '../utils/dashboardSection.utils';

interface UseDashboardActiveSectionsParams {
  phaseSection: any[];
  phaseNow: any;
  openContext: string | null | undefined;
  rulesTasksCardsAll: any[];
}

/**
 * Gestiona qué sección(es) del collapse están abiertas:
 * - Apertura inicial según phaseSection/phaseNow.
 * - Sincronización con openContext (task abierta por URL o desde Loan Details).
 */
export function useDashboardActiveSections(params: UseDashboardActiveSectionsParams) {
  const { phaseSection, phaseNow, openContext, rulesTasksCardsAll } = params;

  const [activeSectionKeys, setActiveSectionKeys] = useState<string[]>([]);
  const hasSetInitialSectionKey = useRef(false);
  const lastOpenContext = useRef<string | null>(null);
  const rulesTasksCardsAllRef = useRef(rulesTasksCardsAll);
  rulesTasksCardsAllRef.current = rulesTasksCardsAll;

  // Abrir la sección inicial cuando hay phaseSection con tasks
  useEffect(() => {
    if (
      !hasSetInitialSectionKey.current &&
      activeSectionKeys.length === 0 &&
      phaseSection?.length > 0 &&
      phaseSection.some(
        (section: any) => Array.isArray(section.tasks) && section.tasks.length > 0
      )
    ) {
      const initialKey = getInitialActiveSectionKey(phaseSection, phaseNow);
      setActiveSectionKeys([initialKey]);
      hasSetInitialSectionKey.current = true;
    }
  }, [phaseSection, activeSectionKeys.length, phaseNow]);

  // Cuando cambia openContext (o llega rulesTasksCardsAll), abrir la sección de esa task
  useEffect(() => {
    if (!openContext || !phaseSection || phaseSection.length === 0) {
      if (!openContext) lastOpenContext.current = null;
      return;
    }

    const list = rulesTasksCardsAllRef.current;
    const contextToSectionMap: { [key: string]: string } = {};
    list.forEach((taskCard) => {
      if (taskCard.modalKey && taskCard.phase?.length > 0) {
        const sectionKey =
          taskCard.phase[0] === 'due_diligence' ? 'due-diligence' : taskCard.phase[0];
        contextToSectionMap[taskCard.modalKey] = sectionKey;
        if (taskCard.key) contextToSectionMap[taskCard.key] = sectionKey;
      }
    });

    const targetSection = contextToSectionMap[openContext];
    if (targetSection) {
      setActiveSectionKeys([targetSection]);
      lastOpenContext.current = openContext;
    }
  }, [openContext, phaseSection, rulesTasksCardsAll]);

  return { activeSectionKeys, setActiveSectionKeys };
}
